import {
  trackToolExperience,
  deviceLayout,
} from "../../features/toolExperience/telemetry";
import { useState, useEffect, useRef } from "react";
import {
  ToolPage,
  ToolHeader,
  CollectionSummary,
} from "../../components/ToolPage";
import BriefingProjectList from "./BriefingProjectList";
import { useTaskStart } from "../../features/toolExperience/useTaskStart";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Modal, { ConfirmModal } from "../../components/Modal/Modal";
import { useBriefingProjects } from "../../hooks/useBriefingProjects";
import { useBriefingAnalyses } from "../../hooks/useBriefingAnalyses";
// import { useBriefingProjectCounts } from '../../hooks/useBriefingProjectCounts'; // Temporarily disabled
import { useToast } from "../../hooks/useToast";
import { useAuth } from "../../contexts/AuthContext";
import { useToolScope } from "../../features/toolExperience/useToolScope";
import { scopeKey as toolScopeKey } from "../../features/toolExperience/types";
import {
  briefSubmissionKey,
  clearBriefSubmissionKey,
  contextCountryCode,
} from "../../utils/siteContext";
import type { SiteContextSelection } from "../../types/siteContext";
import { authenticatedFetch } from "../../api/authenticatedFetch";
import {
  createProjectKnowledgeSource,
  uploadProjectKnowledgeFile,
} from "../../api/briefing";
import { getCmsConfig } from "../../api/cms";
import { CMS_INTEGRATIONS_COMING_SOON } from "../../config/constants";
import { BriefingProjectModal } from "../../components/Briefing/BriefingProjectModal";
import BriefingAnalysesList from "../../components/Briefing/AnalysesList";
import BriefingResultsDisplay from "../../components/Briefing/ResultsDisplay";
import BriefV2View from "../../components/Briefing/V2/BriefV2View";
import BriefV2CreateForm from "../../components/Briefing/V2/BriefV2CreateForm";
import { createBriefV2Analysis, getBriefV2Config } from "../../api/briefing";
import { dispatchTokenExpiredEvent } from "../../utils/authEvents";
import ProjectKnowledgePanel from "../../components/Briefing/ProjectKnowledgePanel";
import ContentOptimizer from "../../components/Briefing/Optimizer/ContentOptimizer";
import { optimizerAPI } from "../../api/contentOptimizer";
import optimizerStyles from "../../components/Briefing/Optimizer/Optimizer.module.css";
import IntegrationsSettings from "../../components/Briefing/CmsIntegrations/IntegrationsSettings";
import type {
  BriefingProject,
  BriefingAnalysis,
  BriefingAnalysisResults,
  BriefingViewMode,
  CreateBriefingProjectData,
} from "../../types/briefing";
import styles from "./BriefingDashboard.module.css";
import {
  buildProjectDefaultsKnowledgeContent,
  PROJECT_DEFAULTS_SOURCE_MARKER,
  PROJECT_DEFAULTS_SOURCE_TITLE,
} from "../../utils/briefingProjectKnowledge";

import { logger } from "../../utils/logger";
import { briefingIntentAPI } from "../../api/briefingIntent";
import {
  clearBriefingIntent,
  getBriefingIntent,
  setBriefingIntent,
  suggestedProjectName,
  type BriefingIntent,
} from "../../utils/briefingIntent";

async function finishHandoffWithRetry(
  id: string,
  action: "complete" | "dismiss",
): Promise<void> {
  for (const delay of [0, 1000, 3000]) {
    if (delay)
      await new Promise((resolve) => window.setTimeout(resolve, delay));
    try {
      await briefingIntentAPI[action](id);
      const local = getBriefingIntent();
      if (
        local?.id === id &&
        local.state === (action === "complete" ? "completed" : "dismissed")
      )
        clearBriefingIntent();
      return;
    } catch (error) {
      logger.warn(
        "Could not mark briefing handoff complete; will retry:",
        error,
      );
    }
  }
  // The local completed state prevents a new brief from opening on this
  // device. AuthContinue retries the checkpoint on the next sign-in.
}

export default function BriefingDashboard() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { user } = useAuth();
  const workspaceScope = useToolScope();
  const contextScopeKey = toolScopeKey(workspaceScope);
  const [optimizerCapability, setOptimizerCapability] = useState({
    key: "",
    enabled: false,
  });
  const optimizerScope = `${contextScopeKey}:${projectId || ""}`;
  const optimizerEnabled =
    optimizerCapability.key === optimizerScope && optimizerCapability.enabled;
  const optimizerTab =
    optimizerEnabled &&
    new URLSearchParams(location.search).get("tool") === "optimizer";
  useEffect(() => {
    const controller = new AbortController();
    if (projectId && workspaceScope?.organizationId) {
      optimizerAPI
        .config(workspaceScope.organizationId, projectId, controller.signal)
        .then((config) => {
          if (!controller.signal.aborted)
            setOptimizerCapability({
              key: optimizerScope,
              enabled: config.enabled,
            });
        })
        .catch(() => {
          if (!controller.signal.aborted)
            setOptimizerCapability({ key: optimizerScope, enabled: false });
        });
    }
    return () => controller.abort();
  }, [projectId, workspaceScope?.organizationId, optimizerScope]);
  const clusterBatchId = new URLSearchParams(location.search).get(
    "cluster_batch",
  );
  const clusterId = new URLSearchParams(location.search).get("cluster");
  const [clusterHandoff, setClusterHandoff] = useState<{
    keyword: string;
    country_code: string;
    language_code: string;
    website_url: string;
    site_context: SiteContextSelection;
  } | null>(null);
  const [clusterHandoffError, setClusterHandoffError] = useState("");
  useEffect(() => {
    let current = true;
    setClusterHandoff(null);
    setClusterHandoffError("");
    if (clusterBatchId && clusterId) {
      Promise.all([
        authenticatedFetch<{
          config: {
            target_domain?: string;
            country?: string;
            country_code?: string;
            language_code?: string;
          };
        }>(`/api/v1/clustering/batches/${encodeURIComponent(clusterBatchId)}`),
        authenticatedFetch<{ id: string; hub_keyword: string }[]>(
          `/api/v1/clustering/batches/${encodeURIComponent(clusterBatchId)}/clusters?limit=5000`,
        ),
      ])
        .then(([batch, clusters]) => {
          if (!current) return;
          const cluster = clusters.find((c) => c.id === clusterId);
          if (!cluster) throw new Error("Selected cluster was not found.");
          const rawCountry = batch.config.country_code || batch.config.country;
          const resolvedCountry = contextCountryCode(rawCountry);
          if (rawCountry && !resolvedCountry)
            throw new Error(
              "The selected cluster has an unsupported country. Reselect its market in Clustering.",
            );
          const country = resolvedCountry || "us";
          const language = batch.config.language_code || "en";
          const website = batch.config.target_domain
            ? /^https?:\/\//.test(batch.config.target_domain)
              ? batch.config.target_domain
              : `https://${batch.config.target_domain}`
            : "";
          setClusterHandoff({
            keyword: cluster.hub_keyword,
            country_code: country,
            language_code: language,
            website_url: website,
            site_context: {
              site_url: website,
              market: { country_code: country, language_code: language },
              clustering_batch_id: clusterBatchId,
              cluster_id: clusterId,
            },
          });
        })
        .catch(() => {
          if (current)
            setClusterHandoffError(
              "The selected cluster could not be loaded. Return to Clustering and select it again.",
            );
        });
    }
    return () => {
      current = false;
    };
  }, [clusterBatchId, clusterId, contextScopeKey]);

  const [viewMode, setViewMode] = useState<BriefingViewMode>("dashboard");
  const [selectedProject, setSelectedProject] =
    useState<BriefingProject | null>(null);
  const [selectedAnalysis, setSelectedAnalysis] =
    useState<BriefingAnalysis | null>(null);
  const [selectedResults, setSelectedResults] =
    useState<BriefingAnalysisResults | null>(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [handoffIntent, setHandoffIntent] = useState<BriefingIntent | null>(
    () => getBriefingIntent(),
  );
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    project: BriefingProject | null;
  }>({ isOpen: false, project: null });
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    project: BriefingProject | null;
  }>({ isOpen: false, project: null });
  const isCreatingProjectRef = useRef(false);

  useEffect(() => {
    if (
      !projectId &&
      new URLSearchParams(location.search).get("create") === "1" &&
      (!handoffIntent || handoffIntent.state === "claimed")
    ) {
      setShowProjectModal(true);
    }
  }, [projectId, location.search, handoffIntent?.id, handoffIntent?.state]);

  const dismissHandoff = () => {
    if (handoffIntent) {
      setBriefingIntent({ ...handoffIntent, state: "dismissed" });
      void finishHandoffWithRetry(handoffIntent.id, "dismiss");
      setHandoffIntent(null);
    }
    navigate(projectId ? `/briefings/${projectId}` : "/briefings", {
      replace: true,
    });
  };

  // Spec 04: CMS integrations capability flag (CMS_INTEGRATIONS_ENABLED) + settings modal
  const [cmsEnabled, setCmsEnabled] = useState(false);
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false);
  // Pre-launch teaser: when true, show a "Coming soon" entry point instead of
  // the (fully built) live Integrations UI. See CMS_INTEGRATIONS_COMING_SOON.
  const [showIntegrationsComingSoon, setShowIntegrationsComingSoon] =
    useState(false);
  const showIntegrationsButton = CMS_INTEGRATIONS_COMING_SOON || cmsEnabled;

  useEffect(() => {
    // Skip the capability probe entirely while the UI is in coming-soon mode —
    // nothing live is exposed, so there's no reason to hit the endpoint.
    if (CMS_INTEGRATIONS_COMING_SOON) return;
    getCmsConfig()
      .then((cfg) => setCmsEnabled(Boolean(cfg.enabled)))
      .catch(() => setCmsEnabled(false));
  }, []);

  // Project management
  const {
    projects,
    isLoading: projectsLoading,
    createProject,
    updateProject,
    deleteProject,
    isAuthenticated: projectsAuthenticated,
    error: projectsError,
    refreshProjects,
  } = useBriefingProjects();

  // Analyses management for selected project (only load when project is selected)
  const {
    analyses,
    isLoading: analysesLoading,
    error: analysesError,
    deleteAnalysis,
    getResults,
    pollResults,
    stopPolling,
    isAuthenticated: analysesAuthenticated,
  } = useBriefingAnalyses(selectedProject?.id);

  const isAuthenticated = projectsAuthenticated && analysesAuthenticated;

  // Handle URL-based project selection
  useEffect(() => {
    if (projectId && projects.length > 0) {
      const project = projects.find((p) => p.id === projectId);
      if (project) {
        setSelectedProject(project);
        const resumeBrief =
          new URLSearchParams(location.search).has("cluster") ||
          (new URLSearchParams(location.search).get("createBrief") === "1" &&
            getBriefingIntent()?.state === "project_created" &&
            getBriefingIntent()?.project_id === project.id);
        setViewMode((current) =>
          current === "brief_v2" || current === "results"
            ? current
            : resumeBrief
              ? "create-brief-v2"
              : "analyses",
        );
      } else {
        // Project not found, redirect to dashboard
        navigate("/briefings");
      }
    } else if (!projectId && selectedProject) {
      // URL doesn't have projectId but we have a selected project, clear it
      setSelectedProject(null);
      setViewMode("dashboard");
    }
  }, [projectId, projects, navigate, selectedProject, location.search]);

  const handleCreateProject = () => {
    setShowProjectModal(true);
  };

  const handleProjectSubmit = async (
    projectData: CreateBriefingProjectData,
  ) => {
    if (isCreatingProjectRef.current) return;
    isCreatingProjectRef.current = true;

    try {
      const newProject = await createProject(
        handoffIntent
          ? { ...projectData, onboarding_intent_id: handoffIntent.id }
          : projectData,
      );
      setShowProjectModal(false);
      toast.success(
        "Project created successfully!",
        `"${newProject.name}" is ready for content briefing analysis.`,
      );

      if (handoffIntent) {
        const nextIntent = {
          ...handoffIntent,
          state: "project_created" as const,
          project_id: newProject.id,
        };
        setBriefingIntent(nextIntent);
        setHandoffIntent(nextIntent);
        try {
          await briefingIntentAPI.projectCreated(
            handoffIntent.id,
            newProject.id,
          );
        } catch (error) {
          logger.warn(
            "Briefing handoff checkpoint will be retried on next sign-in:",
            error,
          );
        }
        navigate(`/briefings/${newProject.id}?createBrief=1`, {
          replace: true,
        });
      }

      if (user?.id && newProject.created_new !== false) {
        try {
          const defaultsContent =
            buildProjectDefaultsKnowledgeContent(projectData);
          if (defaultsContent) {
            await createProjectKnowledgeSource(
              newProject.id,
              {
                source_type: "skills_md",
                title: PROJECT_DEFAULTS_SOURCE_TITLE,
                content: defaultsContent,
                metadata: { created_from: PROJECT_DEFAULTS_SOURCE_MARKER },
              },
              user,
              () => toast.error("Authentication expired"),
            );
          }

          for (const file of projectData.project_knowledge_files || []) {
            await uploadProjectKnowledgeFile(
              newProject.id,
              file,
              undefined,
              user,
              () => toast.error("Authentication expired"),
            );
          }
        } catch (knowledgeError) {
          logger.error(
            "Failed to attach initial Project Knowledge:",
            knowledgeError,
          );
          toast.warning(
            "Project created, but some Project Knowledge was not saved",
            knowledgeError instanceof Error
              ? knowledgeError.message
              : "Please add it from Manage Knowledge.",
          );
        }
      }
      taskStart.created(newProject);
    } catch (error) {
      logger.error("Failed to create project:", error);
      throw error; // Re-throw to prevent modal from closing
    } finally {
      isCreatingProjectRef.current = false;
    }
  };

  // All new briefs use the v2 research and writing workflow.
  const handleCreateAnalysis = (project: BriefingProject) => {
    // Project from list already contains all default parameters
    logger.log("📋 Using project with defaults:", {
      id: project.id,
      brand_characteristics: project.brand_characteristics,
      target_audience: project.target_audience,
      usps_features: project.usps_features,
      customer_pain_points: project.customer_pain_points,
      website_type: project.website_type,
      type_of_article: project.type_of_article,
      constraints: project.constraints,
    });

    setSelectedProject(project);
    navigate(
      `/briefings/${project.id}${clusterBatchId && clusterId ? location.search : ""}`,
    );

    // Set view mode after navigation
    setTimeout(() => setViewMode("create-brief-v2"), 100);
  };

  const taskStart = useTaskStart({
    projects,
    toolId: "briefings",
    onSelect: handleCreateAnalysis,
    onCreate: handleCreateProject,
    loading: projectsLoading,
  });
  const consumedStart = useRef("");
  useEffect(() => {
    if (
      new URLSearchParams(location.search).get("start") !== "brief" ||
      projectsLoading ||
      projectsError ||
      consumedStart.current === location.key
    )
      return;
    consumedStart.current = location.key;
    const params = new URLSearchParams(location.search);
    params.delete("start");
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true },
    );
    taskStart.begin();
  }, [
    location.key,
    location.pathname,
    location.search,
    projectsLoading,
    projectsError,
    navigate,
    taskStart,
  ]);

  const handleProjectDelete = (project: BriefingProject) => {
    setDeleteModal({ isOpen: true, project });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.project) return;

    try {
      await deleteProject(deleteModal.project.id);
      setDeleteModal({ isOpen: false, project: null });
      if (selectedProject?.id === deleteModal.project.id) {
        navigate("/briefings");
      }
      toast.success("Project deleted successfully");
    } catch (error) {
      logger.error("Failed to delete project:", error);
      toast.error("Failed to delete project");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, project: null });
  };

  const handleProjectEdit = (project: BriefingProject) => {
    setEditModal({ isOpen: true, project });
  };

  const handleEditSubmit = async (
    data: Partial<BriefingProject>,
    files?: File[],
  ) => {
    if (!editModal.project) return;

    const projectId = editModal.project.id;

    try {
      await updateProject(projectId, data);

      if (user?.id && files && files.length > 0) {
        try {
          for (const file of files) {
            await uploadProjectKnowledgeFile(
              projectId,
              file,
              undefined,
              user,
              () => toast.error("Authentication expired"),
            );
          }
        } catch (knowledgeError) {
          logger.error(
            "Failed to attach Project Knowledge files:",
            knowledgeError,
          );
          toast.warning(
            "Project updated, but some Project Knowledge was not saved",
            knowledgeError instanceof Error
              ? knowledgeError.message
              : "Please add it from Manage Knowledge.",
          );
        }
      }

      setEditModal({ isOpen: false, project: null });
      toast.success("Project updated successfully");
    } catch (error) {
      logger.error("Failed to update project:", error);
      toast.error("Failed to update project");
    }
  };

  const handleEditCancel = () => {
    setEditModal({ isOpen: false, project: null });
  };

  // 🆕 Brief v2 create (dedicated path — bypasses the combined create hook).
  const [briefV2Submitting, setBriefV2Submitting] = useState(false);
  const [briefV2SubmissionError, setBriefV2SubmissionError] = useState("");
  const currentBriefTarget = useRef("");
  currentBriefTarget.current = `${contextScopeKey}:${selectedProject?.id || ""}`;
  useEffect(() => {
    setBriefV2SubmissionError("");
  }, [contextScopeKey, selectedProject?.id, viewMode]);
  // A disabled v2 capability shows an unavailable state; it never revives v1.
  const [briefV2Enabled, setBriefV2Enabled] = useState<boolean | null>(null);
  const [siteContextEnabled, setSiteContextEnabled] = useState(false);
  useEffect(() => {
    if (!user?.id) return;
    getBriefV2Config(user, () => dispatchTokenExpiredEvent())
      .then((c) => {
        setBriefV2Enabled(!!c?.enabled);
        setSiteContextEnabled(!!c?.site_context_enabled);
      })
      .catch(() => {
        setBriefV2Enabled(null);
        setSiteContextEnabled(false);
      });
  }, [user]);
  const handleCreateBriefV2 = async (form: {
    keyword: string;
    country_code: string;
    language_code: string;
    type_of_article: string;
    recommend_content_type: boolean;
    website_url?: string;
    site_context?: SiteContextSelection;
  }) => {
    if (!selectedProject || briefV2Submitting) return;
    const target = `${contextScopeKey}:${selectedProject.id}`;
    setBriefV2Submitting(true);
    setBriefV2SubmissionError("");
    try {
      // Persist a newly-entered website to the project so the pre-check,
      // internal links, and site-type match can use it (it's a project-level field).
      const website = form.website_url || selectedProject.website_url;
      if (
        form.website_url &&
        form.website_url !== selectedProject.website_url
      ) {
        try {
          await updateProject(selectedProject.id, {
            website_url: form.website_url,
          });
          if (currentBriefTarget.current !== target) return;
          setSelectedProject({
            ...selectedProject,
            website_url: form.website_url,
          });
        } catch (e) {
          if (form.site_context)
            throw new Error(
              "Save the selected website before preparing a connected brief. Please retry.",
            );
          logger.warn(
            "Could not save website to project; continuing with the brief.",
            e,
          );
        }
      }
      if (currentBriefTarget.current !== target) return;
      const analysis = await createBriefV2Analysis(
        {
          project_id: selectedProject.id,
          ...(form.site_context
            ? {
                create_request_id: briefSubmissionKey(
                  `${contextScopeKey}:${selectedProject.id}`,
                  form,
                ),
              }
            : {}),
          input_data: { keyword: form.keyword },
          parameters: {
            country_code: form.country_code,
            language_code: form.language_code,
            type_of_article: form.type_of_article,
            recommend_content_type: form.recommend_content_type,
            website_url: website,
            ...(form.site_context ? { site_context: form.site_context } : {}),
          },
        },
        user,
        () => dispatchTokenExpiredEvent(),
      );
      if (currentBriefTarget.current !== target) return;
      clearBriefSubmissionKey(`${contextScopeKey}:${selectedProject.id}`);
      trackToolExperience("task_submitted", {
        toolId: "briefings",
        source: "page",
        layout: deviceLayout(),
      });
      toast.success("Brief v2 started!", "Running the research pipeline.");
      if (handoffIntent) {
        setBriefingIntent({ ...handoffIntent, state: "completed" });
        void finishHandoffWithRetry(handoffIntent.id, "complete");
        setHandoffIntent(null);
        navigate(`/briefings/${selectedProject.id}`, { replace: true });
      }
      setSelectedAnalysis(analysis);
      setViewMode("brief_v2");
    } catch (error) {
      if (currentBriefTarget.current !== target) return;
      setBriefV2SubmissionError(
        error instanceof Error
          ? error.message
          : "Check the selected sources and retry.",
      );
      logger.error("Failed to create Brief v2:", error);
      toast.error(
        "Failed to start Brief v2",
        error instanceof Error ? error.message : "Unknown error",
      );
    } finally {
      setBriefV2Submitting(false);
    }
  };

  const handleAnalysisSelect = async (analysis: BriefingAnalysis) => {
    // 🆕 Brief v2 analyses have their own two-stage view (self-polling).
    if (analysis.analysis_type === "brief_v2") {
      if (analysis.id.startsWith("temp-")) {
        toast.error("Brief is being created", "Please wait a moment.");
        return;
      }
      if (selectedAnalysis && selectedAnalysis.id !== analysis.id)
        stopPolling(selectedAnalysis.id);
      setSelectedAnalysis(analysis);
      setViewMode("brief_v2");
      return;
    }

    // Safety check: only allow combined analyses to be viewed in results
    if (analysis.analysis_type !== "combined") {
      logger.warn("Attempted to view non-combined analysis:", analysis);
      toast.error(
        "Invalid analysis type",
        "This analysis cannot be viewed as briefing results.",
      );
      return;
    }

    // ⬅️ FIX: Don't allow selecting temporary analyses
    if (analysis.id.startsWith("temp-")) {
      logger.warn("Attempted to view temporary analysis:", analysis.id);
      toast.error(
        "Analysis is being created",
        "Please wait a moment for the analysis to be created.",
      );
      return;
    }

    // ✅ Stop polling previous analysis if any
    if (selectedAnalysis && selectedAnalysis.id !== analysis.id) {
      stopPolling(selectedAnalysis.id);
    }

    setSelectedAnalysis(analysis);
    setViewMode("results");

    // Extract keyword from input_data
    const keyword = analysis.input_data?.keyword || "";

    // ✅ If completed, fetch results immediately
    if (analysis.status === "completed") {
      try {
        const results = await getResults(analysis.id);
        setSelectedResults(results);
      } catch (error) {
        logger.error("Failed to get analysis results:", error);
        toast.error("Failed to load results");
      }
      return;
    }

    // ✅ If processing/pending, start polling (NO initial /results call!)
    if (analysis.status === "processing" || analysis.status === "pending") {
      // Show optimistic state immediately
      const optimisticResults: BriefingAnalysisResults = {
        analysis_id: analysis.id,
        keyword: keyword,
        status: analysis.status,
        analysis_type: "combined",
        progress_percentage: 0,
        current_step: "initializing",
        current_step_name: "Starting analysis...",
      };

      setSelectedResults(optimisticResults);

      // Start polling (this will update progress every 3s)
      pollResults(analysis.id, (updatedResults) => {
        setSelectedResults(updatedResults);

        // Show progress in toast notifications (only at milestones)
        if (updatedResults.progress_percentage !== undefined) {
          const milestones = [15, 40, 55, 75, 85, 100];
          if (milestones.includes(updatedResults.progress_percentage)) {
            const stepInfo = updatedResults.current_step_name
              ? ` - ${updatedResults.current_step_name}`
              : "";
            const etaInfo = updatedResults.estimated_time_remaining_seconds
              ? ` (~${Math.ceil(updatedResults.estimated_time_remaining_seconds / 60)} min remaining)`
              : "";

            toast.info(
              "Analysis in progress",
              `${updatedResults.progress_percentage}% complete${stepInfo}${etaInfo}`,
            );
          }
        }
      })
        .then((finalResults) => {
          setSelectedResults(finalResults);
          toast.success(
            "Analysis completed!",
            "Your content briefing is ready.",
          );
        })
        .catch((error) => {
          // Don't show error if polling was cancelled
          if (
            !error.message.includes("Already polling") &&
            !error.message.includes("cancelled")
          ) {
            logger.error("Analysis polling failed:", error);
            toast.error(
              "Analysis failed",
              error instanceof Error ? error.message : "Unknown error",
            );
          }
        });
    }
  };

  const openedResultLink = useRef("");
  useEffect(() => {
    const analysisId = new URLSearchParams(location.search).get("analysis");
    if (!analysisId) {
      openedResultLink.current = "";
      return;
    }
    const key = `${location.key}:${projectId}:${analysisId}`;
    if (
      !analysisId ||
      !selectedProject ||
      selectedProject.id !== projectId ||
      analysesLoading ||
      openedResultLink.current === key
    )
      return;
    const analysis = analyses.find(
      (item) => item.id === analysisId && item.project_id === projectId,
    );
    if (analysis) {
      openedResultLink.current = key;
      void handleAnalysisSelect(analysis);
    }
    // Consume once per navigation; browser Back can revisit the same result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectId,
    location.key,
    location.search,
    selectedProject,
    analyses,
    analysesLoading,
  ]);

  const handleAnalysisDelete = async (analysisId: string) => {
    try {
      await deleteAnalysis(analysisId);
      // Refresh counts after deleting analysis
      // refreshCounts(); // Temporarily disabled
      toast.success("Analysis deleted successfully");
    } catch (error) {
      logger.error("Failed to delete analysis:", error);
      toast.error("Failed to delete analysis");
    }
  };

  const handleBackToDashboard = () => {
    navigate("/briefings");
  };

  const handleBackToAnalyses = () => {
    if (selectedProject) {
      navigate(`/briefings/${selectedProject.id}`);
      setViewMode("analyses");
      setSelectedResults(null);
    }
  };

  if (projectsLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading content briefing dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={styles.container}>
        <div className={styles.authError}>
          <h2>🔐 Authentication Required</h2>
          <p>Please log in to access the content briefing service.</p>
        </div>
      </div>
    );
  }

  // Results view
  if (viewMode === "results" && selectedResults) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <button
              className={styles.backButton}
              onClick={handleBackToAnalyses}
            >
              ← Back to Analyses
            </button>
            <h1 className={styles.title}>Content Brief Results</h1>
            <p className={styles.subtitle}>
              Analysis for "{selectedResults.keyword}"
            </p>
          </div>
        </div>

        <div className={styles.resultsSection}>
          <BriefingResultsDisplay
            results={selectedResults}
            onBack={handleBackToAnalyses}
            projectId={selectedProject?.id}
          />
        </div>
      </div>
    );
  }

  // 🆕 Brief v2 two-stage view (self-polling research → angle → brief)
  if (viewMode === "brief_v2" && selectedAnalysis) {
    return (
      <BriefV2View
        analysis={selectedAnalysis}
        onBack={() => {
          setSelectedAnalysis(null);
          setViewMode("analyses");
          const params = new URLSearchParams(location.search);
          params.delete("analysis");
          navigate(
            { pathname: location.pathname, search: params.toString() },
            { replace: true },
          );
        }}
      />
    );
  }

  // Analyses view — also the base for the Create Analysis and Create Brief v2
  // flows, which render as modals on top of it (matching the Word Cloud /
  // Similarity create-flow pattern) instead of swapping the whole page out.
  if (
    (viewMode === "analyses" || viewMode === "create-brief-v2") &&
    selectedProject
  ) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <button
              className={styles.backButton}
              onClick={handleBackToDashboard}
            >
              ← Back to Dashboard
            </button>
            <h1 className={styles.title}>
              {optimizerEnabled ? "Content Tools" : "Content Briefs"} –{" "}
              {selectedProject.name}
            </h1>
            <p className={styles.subtitle}>
              {optimizerEnabled
                ? "Create content briefs and improve existing pages"
                : "View and manage content briefing analyses"}
            </p>
          </div>

          <div className={styles.headerActions}>
            {showIntegrationsButton && (
              <button
                className={styles.integrationsButton}
                onClick={() =>
                  CMS_INTEGRATIONS_COMING_SOON
                    ? setShowIntegrationsComingSoon(true)
                    : setShowIntegrationsModal(true)
                }
                title={
                  CMS_INTEGRATIONS_COMING_SOON
                    ? "CMS integrations are coming soon"
                    : undefined
                }
              >
                🔌 Integrations
                {CMS_INTEGRATIONS_COMING_SOON && (
                  <span className={styles.comingSoonPill}>Soon</span>
                )}
              </button>
            )}
            {/* One brief flow. The separate "+ New Brief v2" button was removed
                when v1 creation was deprecated (2026-08-13) — this now opens the
                v2 two-stage workflow via handleCreateAnalysis. */}
            {!optimizerTab && (
              <button
                className={styles.createAnalysisButton}
                onClick={() => handleCreateAnalysis(selectedProject)}
                title="Research → choose an angle → brief"
              >
                + Create New Brief
              </button>
            )}
          </div>
        </div>

        <ProjectKnowledgePanel project={selectedProject} />

        {optimizerEnabled && (
          <div
            className={optimizerStyles.tabs}
            role="tablist"
            aria-label="Content tools"
          >
            {(["briefs", "optimizer"] as const).map((tool) => (
              <button
                key={tool}
                id={`content-${tool}-tab`}
                role="tab"
                aria-selected={
                  tool === "optimizer" ? optimizerTab : !optimizerTab
                }
                aria-controls={`content-${tool}-panel`}
                tabIndex={
                  (tool === "optimizer" ? optimizerTab : !optimizerTab) ? 0 : -1
                }
                onKeyDown={(event) => {
                  if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                    event.preventDefault();
                    document
                      .getElementById(
                        `content-${tool === "briefs" ? "optimizer" : "briefs"}-tab`,
                      )
                      ?.click();
                    document
                      .getElementById(
                        `content-${tool === "briefs" ? "optimizer" : "briefs"}-tab`,
                      )
                      ?.focus();
                  }
                }}
                onClick={() => {
                  setViewMode("analyses");
                  navigate(
                    `/briefings/${selectedProject.id}${tool === "optimizer" ? "?tool=optimizer" : ""}`,
                  );
                }}
              >
                {tool === "briefs" ? "Content Briefs" : "Content Optimizer"}
              </button>
            ))}
          </div>
        )}
        {optimizerTab && workspaceScope ? (
          <div
            role="tabpanel"
            id="content-optimizer-panel"
            aria-labelledby="content-optimizer-tab"
          >
            <ContentOptimizer
              project={selectedProject}
              organizationId={workspaceScope.organizationId}
              selectedReportId={new URLSearchParams(location.search).get(
                "optimization",
              )}
              onSelect={(id) =>
                navigate(
                  `/briefings/${selectedProject.id}?tool=optimizer${id ? `&optimization=${encodeURIComponent(id)}` : ""}`,
                )
              }
            />
          </div>
        ) : (
          <div
            className={styles.analysesSection}
            id="content-briefs-panel"
            role={optimizerEnabled ? "tabpanel" : undefined}
            aria-labelledby={
              optimizerEnabled ? "content-briefs-tab" : undefined
            }
          >
            <BriefingAnalysesList
              analyses={analyses}
              selectedAnalysis={selectedAnalysis}
              onAnalysisSelect={handleAnalysisSelect}
              onAnalysisDelete={handleAnalysisDelete}
              loading={analysesLoading}
              error={analysesError}
            />
          </div>
        )}

        {!optimizerTab &&
          viewMode === "create-brief-v2" &&
          (clusterBatchId && !clusterHandoff ? (
            <div role="status">
              <p>
                {clusterHandoffError || "Loading selected keyword cluster…"}
              </p>
              <button
                type="button"
                onClick={() =>
                  navigate(`/briefings/${selectedProject.id}`, {
                    replace: true,
                  })
                }
              >
                Back to analyses
              </button>
            </div>
          ) : (
            <BriefV2CreateForm
              key={`${contextScopeKey}:${selectedProject.id}:${clusterHandoff?.site_context.cluster_id || ""}`}
              available={briefV2Enabled !== false}
              contextEnabled={siteContextEnabled}
              scopeKey={contextScopeKey}
              project={selectedProject}
              initialValues={
                clusterHandoff
                  ? {
                      ...clusterHandoff,
                      website_url:
                        selectedProject.website_url ||
                        clusterHandoff.website_url,
                      site_context: {
                        ...clusterHandoff.site_context,
                        site_url:
                          selectedProject.website_url ||
                          clusterHandoff.website_url,
                      },
                    }
                  : handoffIntent?.project_id === selectedProject.id
                    ? handoffIntent.payload
                    : undefined
              }
              submitting={briefV2Submitting}
              submissionError={briefV2SubmissionError}
              onSubmit={handleCreateBriefV2}
              onCancel={() => {
                dismissHandoff();
                setViewMode("analyses");
              }}
            />
          ))}

        {!CMS_INTEGRATIONS_COMING_SOON && cmsEnabled && (
          <Modal
            isOpen={showIntegrationsModal}
            onClose={() => setShowIntegrationsModal(false)}
            title="CMS Integrations"
            size="large"
          >
            <IntegrationsSettings projectId={selectedProject.id} />
          </Modal>
        )}

        {CMS_INTEGRATIONS_COMING_SOON && (
          <Modal
            isOpen={showIntegrationsComingSoon}
            onClose={() => setShowIntegrationsComingSoon(false)}
            title="CMS Integrations"
            size="medium"
          >
            <div className={styles.integrationsComingSoon}>
              <span className={styles.integrationsComingSoonIcon}>🚧</span>
              <h3 className={styles.integrationsComingSoonTitle}>
                Coming soon
              </h3>
              <p className={styles.integrationsComingSoonText}>
                Publish articles straight to your site — WordPress, a webhook,
                or a GitHub repository (Next.js, Astro, Hugo &amp; more) —
                without leaving LazySEM. We're putting the finishing touches on
                it; it'll light up here shortly.
              </p>
            </div>
          </Modal>
        )}
      </div>
    );
  }

  // Project collection keeps the creation and result flows owned by this page.
  return (
    <ToolPage toolId="briefings">
      {clusterBatchId && clusterId && (
        <div role="status">
          {clusterHandoffError ||
            "Create a brief from the selected cluster. Choose the target project and verify its website and market."}
          {clusterHandoff && (
            <p>
              Keyword: {clusterHandoff.keyword}. Verify language because legacy
              clusters may not record it.
            </p>
          )}
        </div>
      )}
      <ToolHeader
        title="Briefings"
        primaryAction={{
          label: "New brief",
          onSelect: taskStart.begin,
          disabledReason: projectsError
            ? "Projects are unavailable. Try again first."
            : undefined,
        }}
        secondaryActions={[
          { label: "New project", onSelect: handleCreateProject },
        ]}
      />
      <CollectionSummary
        scope="complete"
        items={[
          { label: "projects", value: projectsError ? null : projects.length },
          {
            label: "briefs",
            value:
              projectsError || projects.some((p) => p.analysis_count == null)
                ? null
                : projects.reduce((n, p) => n + (p.analysis_count || 0), 0),
          },
        ]}
      />
      <BriefingProjectList
        projects={projects}
        error={projectsError}
        onRetry={refreshProjects}
        onEdit={handleProjectEdit}
        onDelete={handleProjectDelete}
        onCreateBrief={handleCreateAnalysis}
      />
      {taskStart.chooser}
      {/* Project Creation Modal */}
      <BriefingProjectModal
        isOpen={showProjectModal}
        mode="create"
        initialValues={
          handoffIntent
            ? {
                name:
                  suggestedProjectName(handoffIntent.payload.website_url) ||
                  `Briefing: ${handoffIntent.payload.keyword}`.slice(0, 100),
                website_url: handoffIntent.payload.website_url,
              }
            : undefined
        }
        initialValuesKey={handoffIntent?.id}
        onClose={() => {
          taskStart.cancel();
          setShowProjectModal(false);
          if (handoffIntent) dismissHandoff();
        }}
        onSubmitCreate={handleProjectSubmit}
      />

      {/* Project Edit Modal */}
      <BriefingProjectModal
        isOpen={editModal.isOpen}
        mode="edit"
        project={editModal.project}
        onClose={handleEditCancel}
        onSubmitEdit={handleEditSubmit}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Briefing Project"
        message={`Are you sure you want to delete "${deleteModal.project?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </ToolPage>
  );
}
